import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  Sms as SmsIcon
} from '@mui/icons-material'

interface TemplatePreviewProps {
  template: {
    name: string
    template_type?: string
    channel?: string
    content: string
    variables: string[]
  }
  onClose?: () => void
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, onClose }) => {
  // Initialize sample data for variables
  const getSampleData = (variableName: string): string => {
    const sampleDataMap: Record<string, string> = {
      customer_name: 'John Doe',
      customer_first_name: 'John',
      order_id: 'ORD-2026-001',
      order_number: 'SO-001',
      order_total: '₹15,000.00',
      order_status: 'Shipped',
      tracking_number: 'DELH123456789',
      payment_link: 'https://pay.example.com/inv-123',
      invoice_number: 'INV-2026-001',
      company_name: 'ABC Company',
      support_email: 'support@example.com',
      support_phone: '+91 98765 43210'
    }
    return sampleDataMap[variableName] || `[${variableName}]`
  }

  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    template.variables.reduce((acc, variable) => {
      acc[variable] = getSampleData(variable)
      return acc
    }, {} as Record<string, string>)
  )

  // Substitute variables in template
  const substituteVariables = (content: string, values: Record<string, string>): string => {
    let result = content
    Object.entries(values).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g')
      result = result.replace(regex, value)
    })
    return result
  }

  const previewContent = substituteVariables(template.content, variableValues)

  // Reset to sample data
  const handleReset = () => {
    setVariableValues(
      template.variables.reduce((acc, variable) => {
        acc[variable] = getSampleData(variable)
        return acc
      }, {} as Record<string, string>)
    )
  }

  // Get template type icon
  const getTemplateIcon = () => {
    const type = (template.template_type || template.channel || '').toLowerCase()
    switch (type) {
      case 'whatsapp':
        return <MessageIcon sx={{ fontSize: 40, color: '#25D366' }} />
      case 'email':
        return <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
      case 'sms':
        return <SmsIcon sx={{ fontSize: 40, color: '#ff9800' }} />
      default:
        return <MessageIcon sx={{ fontSize: 40 }} />
    }
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Template Preview: {template.name}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Preview how your template will look with actual data
      </Typography>

      <Grid container spacing={3}>
        {/* Variable Input Section */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Sample Data
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Edit the values below to see how they appear in the template
            </Typography>

            <Box sx={{ mt: 2 }}>
              {template.variables.length === 0 ? (
                <Alert severity="info">
                  This template has no variables
                </Alert>
              ) : (
                template.variables.map((variable) => (
                  <TextField
                    key={variable}
                    fullWidth
                    label={variable}
                    value={variableValues[variable] || ''}
                    onChange={(e) =>
                      setVariableValues({
                        ...variableValues,
                        [variable]: e.target.value
                      })
                    }
                    sx={{ mb: 2 }}
                    size="small"
                  />
                ))
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {getTemplateIcon()}
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      {template.template_type.toUpperCase()} Message
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      This is how your message will appear
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    minHeight: 200,
                    whiteSpace: 'pre-wrap',
                    fontFamily: template.template_type === 'email' ? 'inherit' : 'monospace',
                    fontSize: template.template_type === 'email' ? '14px' : '13px'
                  }}
                >
                  {previewContent}
                </Box>

                {/* Character Count */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="textSecondary">
                    Character count: {previewContent.length}
                  </Typography>
                  {template.template_type === 'sms' && (
                    <Typography 
                      variant="caption" 
                      color={previewContent.length > 160 ? 'error' : 'textSecondary'}
                    >
                      {previewContent.length > 160 
                        ? `${Math.ceil(previewContent.length / 160)} SMS segments` 
                        : 'Single SMS'}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Warnings */}
            {template.template_type === 'sms' && previewContent.length > 160 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                SMS messages longer than 160 characters will be sent as multiple segments, 
                which may increase costs.
              </Alert>
            )}

            {template.template_type === 'whatsapp' && previewContent.length > 1000 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                WhatsApp messages longer than 1000 characters may be truncated.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Actions */}
      {onClose && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose}>
            Close Preview
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default TemplatePreview
